/**
 * Zooming a page's cards out around a point on screen (a finger starting a drag) and back in
 * around another (where the card landed), so more of the page fits while something is moved.
 *
 * It's a GPU transform (scale, its origin at the finger across — centered on a phone — and the
 * top down) with the page scrolled along each frame so the point under the finger stays put: the
 * cards shrink toward it and the rest of the page comes into view around it. Nothing reflows, so
 * it stays smooth. While zoomed out, the page is only as tall as it looks (a negative bottom
 * margin), so scrolling — by hand or by dragging near an edge — ends where the cards do.
 */

interface Zoomed {
  el: HTMLElement
  scale: number
  raf: number
}
let zoomed: Zoomed | null = null

const OUT_MS = 240
const IN_MS = 200
const ease = (t: number) => 1 - Math.pow(1 - t, 3)

/** How far out: about two screens of cards in view, no smaller than 40%, always a little. */
function scaleFor(height: number): number {
  return Math.min(0.8, Math.max(0.4, (window.innerHeight * 2) / Math.max(1, height)))
}

/** Scale `el` from one size to another, keeping content point `p` (px from its top, unscaled) at screen y `y`. */
function animate(el: HTMLElement, from: number, to: number, p: number, y: number, ms: number, done: () => void) {
  const top = el.getBoundingClientRect().top + window.scrollY
  const start = performance.now()
  const frame = (now: number) => {
    const t = Math.min(1, (now - start) / ms)
    const s = from + (to - from) * ease(t)
    el.style.transform = `scale(${s})`
    window.scrollTo(0, Math.max(0, top + s * p - y))
    if (t < 1) {
      if (zoomed) zoomed.raf = requestAnimationFrame(frame)
    } else done()
  }
  if (zoomed) zoomed.raf = requestAnimationFrame(frame)
}

/** Zoom `el` out around screen point (x, y); `onDone` once it's still (to measure what's where). */
export function zoomOutAround(el: HTMLElement, x: number, y: number, onDone?: () => void) {
  if (zoomed) return
  const rect = el.getBoundingClientRect()
  const height = el.offsetHeight
  const scale = scaleFor(height)
  zoomed = { el, scale, raf: 0 }
  // Around the finger, up and down; across too where there are columns. One column (a phone)
  // stays centered: shrinking toward a thumb at the edge would leave half the screen empty.
  el.style.transformOrigin = `${rect.width < 640 ? rect.width / 2 : x - rect.left}px 0`
  el.style.willChange = 'transform'
  animate(el, 1, scale, y - rect.top, y, OUT_MS, () => {
    // Only as tall as it looks now.
    el.style.marginBottom = `${-(1 - scale) * height}px`
    onDone?.()
  })
}

/** Zoom back in, keeping `anchor` (the card that moved) where it is on screen. */
export function zoomBackIn(anchor?: Element | null, onDone?: () => void) {
  const z = zoomed
  if (!z) return onDone?.()
  cancelAnimationFrame(z.raf)
  const { el, scale } = z
  el.style.marginBottom = ''
  const top = el.getBoundingClientRect().top
  const a = anchor?.getBoundingClientRect()
  const y = a ? a.top : window.innerHeight / 2
  const current = parseFloat(/scale\(([\d.]+)\)/.exec(el.style.transform)?.[1] ?? String(scale))
  animate(el, current, 1, (y - top) / current, y, IN_MS, () => {
    el.style.transform = ''
    el.style.transformOrigin = ''
    el.style.willChange = ''
    zoomed = null
    onDone?.()
  })
}

/** Is a page zoomed out right now (a card being moved)? */
export const isZoomedOut = () => !!zoomed
